import { authApiService } from './authService';

export const audioService = {
  getUserAudioFiles: async () => {
    try {
      const response = await authApiService.get('/audio/files');  
      if (response.data && response.data.statusCode && response.data.body) {
        const isSuccess = response.data.statusCode === 200 || response.data.statusCode === true;
        const processedResponse = {
          success: isSuccess,
          data: response.data.body.data || response.data.body
        };
        return processedResponse;
      }
       return response.data;
    } catch (error) {
      console.error('AudioService: Error fetching user audio files:', error);
      throw error;
    }
  },

getAudioFileUrl: async (sessionId, options = {}) => {
  try {
    // Pre-signed playback URLs are short-lived (15 min); the server clamps to its max (30 min).
    // useAudioCache refreshes them shortly before they expire.
    const { expiresIn = 900, ownerUserId } = options;
    const params = { expiresIn };
    if (ownerUserId) {
      params.ownerUserId = ownerUserId;
    }
    const response = await authApiService.get(`/audio/${sessionId}/url`, { params });
    if (response.status === 202) {
      return {
        success: false,
        pending: true,
        data: response.data?.body?.data || response.data?.data,
      };
    }
    if (response.data && response.data.statusCode && response.data.body) {
      const isSuccess = response.data.statusCode === 200 || response.data.statusCode === true;
      return {
        success: isSuccess,
        data: response.data.body.data,
        pending: response.data.body.data?.uploadStatus === 'pending_s3',
      };
    }
    return response.data;
  } catch (error) {
    if (error?.response?.status === 202) {
      return {
        success: false,
        pending: true,
        data: error.response?.data?.body?.data || error.response?.data?.data,
      };
    }
    console.error('Error getting audio file URL:', error);
    throw error;
  }
},

  checkAudioFileStatus: async (sessionId) => {
    try {
      const response = await authApiService.get(`/audio/${sessionId}/status`);
      if (response.data && response.data.statusCode && response.data.body) {
        const isSuccess = response.data.statusCode === 200 || response.data.statusCode === true;
        return {
          success: isSuccess,
          data: response.data.body.data
        };
      }
      return response.data;
    } catch (error) {
      console.error('Error checking audio file status:', error);
      throw error;
    }
  },

  uploadLiveAudio: async (sessionId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authApiService.post(
        `/audio/${sessionId}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data && response.data.statusCode && response.data.body) {
        const isSuccess =
          response.data.statusCode === 200 || response.data.statusCode === true;
        return {
          success: isSuccess,
          data: response.data.body.data || response.data.body,
        };
      }
      return response.data;
    } catch (error) {
      console.error('Error uploading live session audio:', error);
      throw error;
    }
  },

  /**
   * Persist a live-capture transcript segment under temp-audio/{user}/{session}/.
   * Writes transcript_seg_XXXX.txt and updates transcription.txt with fullText.
   */
  saveTempTranscriptSegment: async (sessionId, { text, fullText, segmentIndex } = {}) => {
    const response = await authApiService.post('/audio/temp/transcript', {
      sessionId,
      text: text ?? fullText ?? '',
      fullText: fullText ?? text ?? '',
      segmentIndex,
    });
    const root = response?.data ?? response;
    const ok =
      root?.success === true ||
      root?.statusCode === true ||
      root?.statusCode === 200;
    const data = root?.body?.data ?? root?.data ?? root?.body ?? {};
    return { success: ok, data };
  },

  getTempTranscript: async (sessionId) => {
    const response = await authApiService.get('/audio/temp/transcript', {
      params: { sessionId },
    });
    const root = response?.data ?? response;
    const ok =
      root?.success === true ||
      root?.statusCode === true ||
      root?.statusCode === 200;
    const data = root?.body?.data ?? root?.data ?? root?.body ?? {};
    return { success: ok, data };
  },

  mergeTempTranscript: async (sessionId, finalText = '') => {
    const response = await authApiService.post('/audio/temp/transcript/merge', {
      sessionId,
      finalText,
    });
    const root = response?.data ?? response;
    const ok =
      root?.success === true ||
      root?.statusCode === true ||
      root?.statusCode === 200;
    const data = root?.body?.data ?? root?.data ?? root?.body ?? {};
    return { success: ok, data };
  },

  /**
   * Upload one pause/park audio segment as a temp chunk (local + S3 when configured).
   */
  uploadTempAudioChunk: async (sessionId, file, chunkIndex, { isFinal = false, patientId = '' } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', sessionId);
    formData.append('patientId', patientId);
    formData.append('chunkIndex', String(chunkIndex));
    formData.append('isFinal', isFinal ? 'true' : 'false');

    const response = await authApiService.post('/audio/temp/chunk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const root = response?.data ?? response;
    const ok =
      root?.success === true ||
      root?.statusCode === true ||
      root?.statusCode === 200;
    const data = root?.body?.data ?? root?.data ?? root?.body ?? {};
    return { success: ok, data };
  },

  getTempAudioState: async (sessionId, patientId = '') => {
    const response = await authApiService.get('/audio/temp/state', {
      params: { sessionId, patientId },
    });
    const root = response?.data ?? response;
    const ok =
      root?.success === true ||
      root?.statusCode === true ||
      root?.statusCode === 200;
    const data = root?.body?.data ?? root?.data ?? root?.body ?? {};
    return { success: ok, data };
  },

  /**
   * Merge all temp audio chunks for the session and upload one live session file to S3.
   */
  combineTempAndUploadLive: async (sessionId, patientId = '') => {
    const response = await authApiService.post('/audio/temp/combine-upload-live', {
      sessionId,
      patientId,
    });
    const root = response?.data ?? response;
    const ok =
      root?.success === true ||
      root?.statusCode === true ||
      root?.statusCode === 200;
    const data = root?.body?.data ?? root?.data ?? root?.body ?? {};
    return { success: ok, data };
  },
};